import { useCallback, useMemo } from 'react';
import { AnimatePresence, m } from 'framer-motion';
import styled from 'foundations-theming/utils';
import { MilestoneTable } from 'deal-plans-lib/components/table/MilestoneTable';
import { MilestoneRowCells } from 'deal-plans-lib/components/table/MilestoneRowCells';
import { StyledTable } from 'deal-plans-lib/components/table/StyledTable';
import { useCreateMilestone, useUpdateMilestone, useDeleteMilestone, getMilestoneClientKey } from '../hooks/useMilestoneActions';
import { useGetOwnerCandidatesQuery } from '../../__generated__/chirp/com/hubspot/deal/plans/rpc/DealPlanPortalRpc.hooks';
import { usePortalTracking } from '../hooks/usePortalTracking';
import { useWidth } from 'deal-plans-lib/hooks/useWidth';
import { PORTAL_FULL_MIN_WIDTH, PORTAL_MID_MIN_WIDTH } from '../constants/portalBreakpoints';
import { AvatarDisplay } from 'avatar-components/AvatarDisplay';
import { getMilestoneAssigneeName } from 'deal-plans-lib/utils/ownerUtils';
import { useBrandLogo } from '../contexts/PortalPlatformDataContext';
import { useOwnerSrc } from '../hooks/useOwnerSrc';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledMotionRow = styled(m.tr).withConfig({
  displayName: "DealPlanPortalTable__StyledMotionRow"
})(["", ""], ({
  theme,
  selected
}) => selected && `background-color: ${theme.color['fill-field-pressed']};`);
function getTableSizeFromWidth(width) {
  if (width === undefined) return 'full';
  if (width >= PORTAL_FULL_MIN_WIDTH) return 'full';
  if (width >= PORTAL_MID_MIN_WIDTH) return 'mid';
  return 'small';
}
export function DealPlanPortalTable({
  dealPlanId,
  milestones,
  isLoading = false,
  onToggleMilestoneSidebar,
  selectedMilestoneId,
  dealId
}) {
  const {
    createMilestone
  } = useCreateMilestone(dealPlanId);
  const {
    updateMilestone
  } = useUpdateMilestone();
  const {
    deleteMilestone
  } = useDeleteMilestone();
  const {
    trackAddMilestone,
    trackEditMilestone,
    trackDeleteMilestone
  } = usePortalTracking(dealId);
  const [containerRef, containerWidth] = useWidth();
  const size = getTableSizeFromWidth(containerWidth);
  const brandLogo = useBrandLogo();
  const getOwnerSrc = useOwnerSrc();
  const renderAvatar = useCallback(owner => /*#__PURE__*/_jsx(AvatarDisplay, {
    src: getOwnerSrc(owner) || undefined,
    type: "company",
    displayName: getMilestoneAssigneeName(owner),
    size: "xs"
  }), [getOwnerSrc]);
  const {
    data: ownerCandidatesData,
    loading: ownerCandidatesLoading
  } = useGetOwnerCandidatesQuery({
    variables: {
      objectId: dealPlanId
    }
  });
  const orderedMilestones = useMemo(() => milestones.toSorted((a, b) => {
    if (a.dueDate == null && b.dueDate == null) return 0;
    if (a.dueDate == null) return 1;
    if (b.dueDate == null) return -1;
    return a.dueDate - b.dueDate;
  }), [milestones]);
  const milestonesLayoutKey = useMemo(() => orderedMilestones.map(ms => getMilestoneClientKey(ms.milestoneId)).join(), [orderedMilestones]);
  const handleSubmit = useCallback(title => {
    createMilestone(title);
    trackAddMilestone();
  }, [createMilestone, trackAddMilestone]);
  const handleUpdateTitle = useCallback((milestone, subject) => {
    updateMilestone(milestone, {
      subject
    });
    trackEditMilestone('name');
  }, [updateMilestone, trackEditMilestone]);
  const handleUpdateStatus = useCallback((milestone, status) => {
    updateMilestone(milestone, {
      status
    });
    trackEditMilestone('status');
  }, [updateMilestone, trackEditMilestone]);
  const handleUpdateDueDate = useCallback((milestone, dueDate) => {
    updateMilestone(milestone, {
      dueDate
    });
    trackEditMilestone('due date');
  }, [updateMilestone, trackEditMilestone]);
  const handleOwnerChange = useCallback((milestone, owner) => {
    updateMilestone(milestone, {
      owner: owner
    });
    trackEditMilestone('owner');
  }, [updateMilestone, trackEditMilestone]);
  const handleDelete = useCallback(milestoneId => {
    deleteMilestone(milestoneId);
    trackDeleteMilestone();
  }, [deleteMilestone, trackDeleteMilestone]);
  return /*#__PURE__*/_jsx("div", {
    ref: containerRef,
    children: /*#__PURE__*/_jsx(StyledTable, {
      children: /*#__PURE__*/_jsx(MilestoneTable, {
        isLoading: isLoading,
        size: size,
        onSubmit: handleSubmit,
        children: /*#__PURE__*/_jsx(AnimatePresence, {
          children: orderedMilestones.map(milestone => {
            var _ownerCandidatesData$;
            return /*#__PURE__*/_jsx(StyledMotionRow, {
              "data-milestone-id": milestone.milestoneId,
              selected: selectedMilestoneId === milestone.milestoneId,
              layout: "position",
              layoutDependency: milestonesLayoutKey,
              initial: false,
              exit: milestone.milestoneId > 0 ? {
                opacity: 0
              } : undefined,
              transition: {
                y: {
                  duration: 0.3
                }
              },
              children: /*#__PURE__*/_jsx(MilestoneRowCells, {
                milestone: milestone,
                onUpdateTitle: handleUpdateTitle,
                onUpdateStatus: handleUpdateStatus,
                onUpdateDueDate: handleUpdateDueDate,
                onDelete: handleDelete,
                ownerCandidates: (_ownerCandidatesData$ = ownerCandidatesData === null || ownerCandidatesData === void 0 ? void 0 : ownerCandidatesData.candidates) !== null && _ownerCandidatesData$ !== void 0 ? _ownerCandidatesData$ : [],
                onOwnerChange: handleOwnerChange,
                ownerCandidatesLoading: ownerCandidatesLoading,
                renderAvatar: renderAvatar,
                sellerGroupName: brandLogo === null || brandLogo === void 0 ? void 0 : brandLogo.name,
                onToggleMilestoneSidebar: onToggleMilestoneSidebar
              })
            }, getMilestoneClientKey(milestone.milestoneId));
          })
        })
      })
    })
  });
}